import { ReactNode } from 'react';
import { Link, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useCurrentUser, useLogout } from '@/hooks/useAuth';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';

type AdminUser = {
  id: string; email: string | null; name: string | null;
  pictureUrl: string | null; isSiteAdmin?: boolean;
};

const NAV = [
  { to: '/admin', end: true, labelKey: 'admin.nav.overview', fallback: 'Overview' },
  { to: '/admin/users', labelKey: 'admin.nav.users', fallback: 'Users' },
  { to: '/admin/companies', labelKey: 'admin.nav.companies', fallback: 'Companies' },
  { to: '/admin/emails', labelKey: 'admin.nav.emails', fallback: 'Emails' },
  { to: '/admin/audit', labelKey: 'admin.nav.audit', fallback: 'Audit trail' },
];

/**
 * Cross-tenant admin shell. Renders only when the signed-in user has
 * isSiteAdmin = TRUE. Otherwise bounces back to /app.
 */
export function AdminShell({
  children,
  title,
  description,
  actions,
}: {
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  const { t } = useTranslation();
  const { data: user, isLoading } = useCurrentUser();

  if (isLoading) {
    return (
      <div className="min-h-screen paper-archive flex items-center justify-center text-ink-500 text-sm">
        {t('common.loading', { defaultValue: 'Loading…' })}
      </div>
    );
  }
  if (!user) return <Navigate to="/?auth=required" replace />;
  const u = user as unknown as AdminUser;
  if (!u.isSiteAdmin) return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen paper-archive">
      <header className="sticky top-0 z-30 bg-paper-50/85 backdrop-blur-md border-b border-ink-100/80">
        <div className="px-5 sm:px-8 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-5">
            <Link to="/admin" className="inline-flex items-center gap-2 font-semibold tracking-tight text-ink-900">
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-warn-500 text-white text-[11px] font-bold shadow-press">⚑</span>
              <span className="text-[15px]">{t('admin.title', { defaultValue: 'Admin' })}</span>
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-md text-[13px] transition ${
                      isActive
                        ? 'bg-white text-ink-900 shadow-press border border-ink-100'
                        : 'text-ink-600 hover:text-ink-900 hover:bg-white/60'
                    }`
                  }
                >
                  {t(n.labelKey, { defaultValue: n.fallback })}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/app" className="text-[12px] text-ink-500 hover:text-ink-900 transition">
              ↗ {t('admin.back_to_app', { defaultValue: 'Back to app' })}
            </Link>
            <LanguageSwitcher />
            <UserMenu user={u} />
          </div>
        </div>
        <nav className="md:hidden border-t border-ink-100 px-3 py-2 flex gap-1 overflow-x-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-md text-[13px] transition ${
                  isActive ? 'bg-white text-ink-900 border border-ink-100' : 'text-ink-600'
                }`
              }
            >
              {t(n.labelKey, { defaultValue: n.fallback })}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="px-5 sm:px-8 py-8">
        <div className="mx-auto max-w-6xl">
          {(title || actions) && (
            <header className="flex flex-wrap items-end justify-between gap-4 mb-7">
              <div>
                {title && (
                  <h1 className="display text-[26px] font-semibold tracking-tight text-ink-900 leading-tight">
                    {title}
                  </h1>
                )}
                {description && (
                  <p className="mt-1.5 text-[14px] text-ink-600 leading-relaxed max-w-2xl">
                    {description}
                  </p>
                )}
              </div>
              {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
            </header>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}

function UserMenu({ user }: { user: AdminUser }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const logout = useLogout();
  const navigate = useNavigate();

  const initials = (() => {
    const n = (user.name || user.email || '?').trim();
    const parts = n.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  })();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 h-9 pl-1 pr-2 rounded-full hover:bg-ink-100/70 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
      >
        {user.pictureUrl ? (
          <img src={user.pictureUrl} alt="" className="h-7 w-7 rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span className="h-7 w-7 inline-flex items-center justify-center rounded-full bg-warn-500 text-white text-[11px] font-semibold">
            {initials}
          </span>
        )}
        <svg viewBox="0 0 12 12" className="h-3 w-3 text-ink-500" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" d="M3 4.5l3 3 3-3" />
        </svg>
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.12 }}
              className="absolute right-0 mt-2 w-60 z-40 rounded-xl border border-ink-100 bg-white shadow-card overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-ink-100">
                <div className="text-[13px] font-semibold text-ink-900 truncate">
                  {user.name || t('nav.signed_in', { defaultValue: 'Signed in' })}
                </div>
                {user.email && (
                  <div className="text-[12px] text-ink-500 truncate">{user.email}</div>
                )}
                <div className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.12em] text-warn-600 font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-warn-500" />
                  {t('admin.site_admin_badge', { defaultValue: 'Site admin' })}
                </div>
              </div>
              <ul className="py-1 text-[13px]">
                <li>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); navigate('/app'); }}
                    className="w-full text-left px-4 py-2 text-ink-700 hover:bg-ink-50 hover:text-ink-900"
                  >
                    {t('admin.back_to_app', { defaultValue: 'Back to app' })}
                  </button>
                </li>
                <div className="my-1 h-px bg-ink-100" />
                <li>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); void logout(); }}
                    className="w-full text-left px-4 py-2 text-warn-600 hover:bg-warn-500/5"
                  >
                    {t('nav.logout', { defaultValue: 'Sign out' })}
                  </button>
                </li>
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
